import { useState, useEffect } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import axios from '../../api/axios';
import useAuth from '../../hooks/useAuth';
import MessageBox from '../MessageBox/MessageBox';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import logo from '../../assets/yapa-logo.png';
import AuthorsModal from '../AuthorsModal/AuthorsModal';
import './LoginPage.css';

interface IFormInput {
  email: string;
  password: string;
  confirm: string;
  admin: string;
}

const EMAIL_REGEX = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
const PASS_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%]).{8,24}$/;

const REGISTER_URL = '/auth/register';
const LOGIN_URL = '/auth/login';

const REGISTER_SUCCESS_MESSAGE = 'Konto zostało poprawnie założone!';
const CONNECTION_ERROR_MESSAGE = 'Nie można nawiązać połączenia z serwerem!';
const INVALID_CREDENTIALS_MESSAGE = 'Podane dane są nieprawidłowe!';
const UNKNOWN_LOGIN_ERROR_MESSAGE = 'Podczas logowania wystąpił nieznany błąd!';
const DUPLICATE_EMAIL = 'Konto o takim adresie email już istnieje!';
const UNKNOWN_ERROR_MESSAGE = 'Nieznany błąd!';
const LOGIN_BUTTON_TITILE = 'ZALOGUJ SIĘ';
const REGISTER_BUTTON_TITLE = 'ZAREJESTRUJ SIĘ';

const LoginPage = () => {
  const { setAuth, registrationDisabled, setRegistrationDisabled } = useAuth();
  const navigate = useNavigate();

  const [login, setLogin] = useState(true);
  const [key, setKey] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isAuthorsModalOpen, setIsAuthorsModalOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setErrorMsg('');
    }
  }, [open]);

  const fetchRegistrationStatus = async () => {
    try {
      const response = await axios.get('/auth/registrationStatus');
      const registrationEnabled = response.data.registrationEnabled;
      setRegistrationDisabled(!registrationEnabled);
    } catch (error) {
      console.error('Error fetching registration status:', error);
    }
  };

  useEffect(() => {
    fetchRegistrationStatus();
  }, []);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: {
      email: '',
      password: '',
      confirm: '',
      admin: '',
    },
  });

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    const { email, password, admin } = data;

    try {
      const response = await axios.post(
        login ? LOGIN_URL : REGISTER_URL,
        JSON.stringify({ 
          email, 
          password, 
          adminPassword: admin
        }),
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        }
      );

      if (login) {
        const accessToken = response?.data?.accessToken;
        const role = response?.data?.role;
        sessionStorage.removeItem('editedBandId');
        setAuth({ email, password, accessToken, role });
      } else {
        setErrorMsg(REGISTER_SUCCESS_MESSAGE);
        setOpen(true);
      }

      reset();

      if (login) {
        if (response?.data?.role === 'admin') {
          navigate('/admin');
        } else if (response?.data?.role === 'program') {
          try {
            const accessToken = response?.data?.accessToken;
            const authHeaders = {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            };

            const concertsResponse = await axios.get('/concerts/all', {
              headers: authHeaders,
              withCredentials: true
            });

            if (concertsResponse.data && concertsResponse.status === 200) {
              const concerts = concertsResponse.data;
              const now = new Date();

              const parseConcertDateTime = (concert: any): Date | null => {
                const dateMatch = typeof concert?.dataKoncertu === "string"
                  ? concert.dataKoncertu.match(/^(\d{2})-(\d{2})-(\d{4})$/)
                  : null;
                const timeSource = typeof concert?.godzinaRozpoczecia === "string"
                  ? concert.godzinaRozpoczecia
                  : "00:00";
                const timeMatch = timeSource.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);

                if (!dateMatch || !timeMatch) return null;

                const day = Number(dateMatch[1]);
                const month = Number(dateMatch[2]);
                const year = Number(dateMatch[3]);
                const hours = Number(timeMatch[1]);
                const minutes = Number(timeMatch[2]);
                const seconds = Number(timeMatch[3] ?? 0);

                const parsedDate = new Date(year, month - 1, day, hours, minutes, seconds, 0);

                if (
                  parsedDate.getFullYear() !== year ||
                  parsedDate.getMonth() !== month - 1 ||
                  parsedDate.getDate() !== day ||
                  parsedDate.getHours() !== hours ||
                  parsedDate.getMinutes() !== minutes ||
                  parsedDate.getSeconds() !== seconds
                ) {
                  return null;
                }

                return parsedDate;
              };

              const parseDurationToMinutes = (duration: string): number => {
                const match = typeof duration === "string"
                  ? duration.match(/^(\d{2}):(\d{2}):(\d{2})$/)
                  : null;
                if (!match) return 0;
                return Number(match[1]) * 60 + Number(match[2]) + Number(match[3]) / 60;
              };

              const getConcertEndDateTime = async (concert: any, startDateTime: Date): Promise<Date> => {
                try {
                  const scheduleResponse = await axios.get(`/concertsSchedule/${concert.idKoncertu}`, {
                    headers: authHeaders,
                    withCredentials: true
                  });
                  const scheduleItems = Array.isArray(scheduleResponse.data) ? scheduleResponse.data : [];
                  const totalDurationMinutes = scheduleItems.reduce((sum: number, item: any) => {
                    return sum + parseDurationToMinutes(item.czasTrwania);
                  }, 0);
                  if (totalDurationMinutes <= 0) return startDateTime;
                  return new Date(startDateTime.getTime() + totalDurationMinutes * 60 * 1000);
                } catch {
                  return startDateTime;
                }
              };

              const concertsWithTiming = (await Promise.all(
                concerts.map(async (concert: any) => {
                  const concertDateTime = parseConcertDateTime(concert);
                  if (!concertDateTime) return null;
                  const concertEndDateTime = await getConcertEndDateTime(concert, concertDateTime);
                  return { ...concert, concertDateTime, concertEndDateTime };
                })
              ))
                .filter((concert: any) => concert !== null)
                .sort((a: any, b: any) => a.concertDateTime.getTime() - b.concertDateTime.getTime());

              const ongoingConcerts = concertsWithTiming.filter(
                (concert: any) => concert.concertDateTime <= now && now < concert.concertEndDateTime
              );

              const upcomingConcerts = concertsWithTiming.filter(
                (concert: any) => concert.concertDateTime >= now
              );

              if (ongoingConcerts.length === 0 && upcomingConcerts.length === 0) {
                navigate('/concertList');
              } else {
                const closestConcert = ongoingConcerts.length > 0
                  ? ongoingConcerts.reduce((prev: any, curr: any) => {
                      const prevStart = prev.concertDateTime.getTime();
                      const currStart = curr.concertDateTime.getTime();
                      if (currStart === prevStart) return curr.idKoncertu < prev.idKoncertu ? curr : prev;
                      return currStart < prevStart ? curr : prev;
                    }, ongoingConcerts[0])
                  : upcomingConcerts.reduce((prev: any, curr: any) => {
                      const prevTime = prev.concertDateTime.getTime();
                      const currTime = curr.concertDateTime.getTime();
                      if (currTime === prevTime) return curr.idKoncertu < prev.idKoncertu ? curr : prev;
                      return currTime < prevTime ? curr : prev;
                    }, upcomingConcerts[0]);

                navigate(`/concert/${closestConcert.idKoncertu}`);
              }
            } else {
              navigate('/concertList');
            }
          } catch (error) {
            console.error("Error fetching concert data:", error);
            navigate('/concertList');
          }
        } else {
          navigate('/band');
        }
      } else {
        setLogin(true);
      }
    } catch (error) {
      if (isAxiosError(error)) {
        if (!error?.response) {
          setErrorMsg(CONNECTION_ERROR_MESSAGE);
        } else if (error.response?.status === 409) {
          setErrorMsg(DUPLICATE_EMAIL);
        } else if (error.response?.status) {
          setErrorMsg(INVALID_CREDENTIALS_MESSAGE);
        } else {
          setErrorMsg(UNKNOWN_LOGIN_ERROR_MESSAGE);
        }
      } else {
        setErrorMsg(UNKNOWN_ERROR_MESSAGE);
      }

      setOpen(true);
    }
  };

  return (
    <div className="login-container">
      <img src={logo} width={250} alt="Logo YAPA" />
      <div className="login-content">
        <div className="button-group-container">
          <ButtonGroup size="large">
            <Button
              variant={login ? 'contained' : 'outlined'}
              onClick={() => {
                setLogin(true);
                setKey(false);
              }}
            >
              LOGOWANIE
            </Button>
            <Button
              variant={login ? 'outlined' : 'contained'}
              onClick={() => {
                if (registrationDisabled) {
                  setErrorMsg('Rejestracja jest obecnie wyłączona przez administratora!');
                  setOpen(true);
                } else {
                  setLogin(false);
                }
              }}
              sx={{
                opacity: registrationDisabled ? 0.5 : 1,
                cursor: registrationDisabled ? 'not-allowed' : 'pointer',
              }}
              title={registrationDisabled ? 'Rejestracja jest obecnie wyłączona' : ''}
            >
              REJESTRACJA
            </Button>
          </ButtonGroup>
        </div>
        <form className="login-form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="email"
            control={control}
            rules={{
              required: true,
              pattern: !login ? EMAIL_REGEX : undefined,
            }}
            render={({ field }) => (
              <TextField
                type="text"
                label="E-mail"
                {...field}
                error={errors.email ? true : false}
                helperText={errors.email ? 'Niepoprawny e-mail' : ''}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={{
              required: true,
              pattern: !login ? PASS_REGEX : undefined,
            }}
            render={({ field }) => (
              <Tooltip
                title={
                  !login && (
                    <p style={{ fontSize: '0.8rem' }}>
                      - Hasło musi mieć od 8 do 24 znaków, w tym:
                      <br />
                      - dużą i małą literę
                      <br />
                      - cyfrę
                      <br />- znak specjalny ( ! @ # $ % )
                    </p>
                  )
                }
                placement="right"
                arrow
                disableHoverListener
              >
                <TextField
                  type="password"
                  label="Hasło"
                  {...field}
                  error={errors.password ? true : false}
                  helperText={errors.password ? 'Niepoprawne hasło' : ''}
                />
              </Tooltip>
            )}
          />
          {login && (
            <Link
              component="a"
              onClick={() => {
                navigate('/ChangePasswordPage');
              }}
            >
              Zapomniałeś hasła? kliknij tu
            </Link>
          )}
          {!login && (
            <>
              <Controller
                name="confirm"
                control={control}
                rules={{
                  required: true,
                  validate: (confirm, { password }) => password === confirm,
                }}
                render={({ field }) => (
                  <TextField
                    type="password"
                    label="Potwierdź Hasło"
                    {...field}
                    error={errors.confirm ? true : false}
                    helperText={errors.confirm ? 'Hasła nie są takie same' : ''}
                  />
                )}
              />
              {key ? (
                <Controller
                  name="admin"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="text"
                      label="Klucz dostępu"
                      {...field}
                    />
                  )}
                />
              ) : (
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(e) => setKey(e.target.checked)}
                      style={{ color: '#ff9100' }}
                    />
                  }
                  label="Administrator?"
                />
              )}
            </>
          )}
          <Button
            variant="contained"
            type="submit"
            size="large"
            sx={{ marginTop: '20px' }}
          >
            {login ? LOGIN_BUTTON_TITILE : REGISTER_BUTTON_TITLE}
          </Button>
        </form>
        <Button
          variant="outlined"
          onClick={() => setIsAuthorsModalOpen(true)}
          sx={{
            borderColor: '#ff9100',
            color: '#ff9100',
            minWidth: '120px',
            marginTop: '-10px',
            '&:hover': {
              borderColor: '#b26500',
              backgroundColor: 'rgba(255, 145, 0, 0.04)',
            },
          }}
        >
          AUTORZY
        </Button>
      </div>
      <MessageBox
        key={errorMsg}
        open={open}
        setOpen={setOpen}
        message={errorMsg}
        error={errorMsg !== REGISTER_SUCCESS_MESSAGE}
      />
      <AuthorsModal 
        isOpen={isAuthorsModalOpen}
        onClose={() => setIsAuthorsModalOpen(false)}
      />
    </div>
  );
};

export default LoginPage;